import { useEffect, useMemo, useRef } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import type { ModuleKey, PageKey } from '@erp/rpc';
import type { AgentModuleConfig } from '@/context/agent-container.context';
import { useAgentModule } from '@/hooks/useAgentModule';
import type { FrontendToolDefinition } from '@/hooks/useFrontendTool';
import { renderSharedToolResult } from '@/components/common/chat/useChatToolResultRenderer';
import { useNavigablePages } from '@/components/nami/useNavigablePages';
import type {
  NamiSurfaceContext,
  NamiSurfaceEntity,
  NamiSurfacePageContext,
} from './namiSurfaceContext';

const OMS_MODULE: ModuleKey = 'oms';

/** Stable per-object tokens so identity can be folded into a memo key. */
const identityTokens = new WeakMap<object, number>();
let nextIdentityToken = 1;
function identityKey(definition: FrontendToolDefinition): string {
  let token = identityTokens.get(definition);
  if (token === undefined) {
    token = nextIdentityToken++;
    identityTokens.set(definition, token);
  }
  return `${token}${definition.enabled === false ? '!' : ''}`;
}

export interface NamiSurfaceOptions {
  moduleKey?: ModuleKey;
  pageKey: PageKey;
  /** Translated page title for the panel's context line. */
  title: string;
  entity?: NamiSurfaceEntity;
  /** Read at send time (ref-wrapped): the page's state as the prompt should see it. */
  getContext: () => NamiSurfacePageContext;
  /**
   * Tool groups from the page's tool hooks. Each group must be identity-stable
   * (every hook here returns a memoized array); the groups are flattened into
   * one memoized array so useAgentModule re-registers only when a tool changes.
   */
  tools: ReadonlyArray<readonly FrontendToolDefinition[]>;
  /** Translated opening prompts shown as chips while the thread is empty. */
  suggestions?: string[];
  placeholder?: string;
  /** Extra per-page cards; falls back to the shared renderer. */
  renderToolResult?: AgentModuleConfig['renderToolResult'];
}

/**
 * Registers an OMS page as an agent surface: the `oms` chat module, the page
 * key the backend layers its prompt on, the page's browser-executed tools, and
 * the on-screen context the model reads.
 */
export function useNamiSurface(options: NamiSurfaceOptions): void {
  const gt = useGT();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pages = useNavigablePages();

  const getContextRef = useRef(options.getContext);
  useEffect(() => {
    getContextRef.current = options.getContext;
  });
  const pagesRef = useRef(pages);
  useEffect(() => {
    pagesRef.current = pages;
  });

  // Manual useMemo on purpose (react-doctor flags it): the flattened array is
  // useAgentModule's `tools` dep, so its identity must follow the tool
  // DEFINITIONS' identities, not this render's group arrays (a page may build
  // `[a, b]` inline). Definitions are identity-stable per useFrontendTool, so
  // a key over their identities is the exact invalidation we want.
  const flat = options.tools.flat();
  const toolsKey = flat.map(identityKey).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tools = useMemo(() => flat, [toolsKey]);

  const { pageKey, title, entity, suggestions, renderToolResult } = options;
  useAgentModule({
    moduleKey: options.moduleKey ?? OMS_MODULE,
    pageKey,
    currentPage: pathname,
    surfaceTitle: title,
    entity,
    suggestions,
    placeholder:
      options.placeholder ??
      gt('Ask about this page…', {
        context: 'placeholder for the AI assistant input on an OMS page',
      }),
    getModuleContext: (): NamiSurfaceContext => ({
      surface: { pageKey, path: pathname, title, entity },
      ...getContextRef.current(),
      today: new Date().toISOString(),
      // Without this the navigation tools have an empty catalog on OMS pages
      // and "take me to sales orders" is refused.
      pages: pagesRef.current,
    }),
    renderToolResult:
      renderToolResult ??
      ((_message, toolResult) =>
        renderSharedToolResult(toolResult, pagesRef.current)),
    tools,
  });
}
